import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as pdfjsLib from 'pdfjs-dist';
import { trackDownload } from '../services/api';

// Configurer le worker
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

/**
 * SimplePDFViewer
 * @param {Object} pdfConfig - { url, httpHeaders }
 * @param {boolean} headless - Si true, masque la toolbar native (le parent gère les contrôles)
 * @param {Function} onPdfLoaded - Callback({ numPages }) quand le PDF est chargé
 * @param {number} externalPage - Page contrôlée par le parent (mode headless)
 * @param {number} externalScale - Zoom contrôlé par le parent (mode headless)
 */
export default function SimplePDFViewer({ pdfConfig, headless = false, onPdfLoaded, externalPage, externalScale }) {
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [pdf, setPdf] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.8); // sera mis à jour depuis la DB
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [flashMsg, setFlashMsg] = useState(null);
  const [allowDownload, setAllowDownload] = useState(true); // sera mis à jour depuis la DB

  // Charger les paramètres viewer depuis l'API (persistés en DB)
  useEffect(() => {
    const API_URL = import.meta.env.VITE_API_URL || '/cv/api';
    fetch(`${API_URL}/site-settings`)
      .then(r => r.json())
      .then(d => {
        const s = d.settings || {};
        if (s.viewer_default_zoom) setScale(parseInt(s.viewer_default_zoom) / 100);
        if (s.viewer_allow_download !== undefined) setAllowDownload(s.viewer_allow_download === 'true');
      })
      .catch(() => {});
  }, []);

  // En mode headless, le parent contrôle page et zoom
  const currentPage = headless && externalPage ? externalPage : pageNum;
  const currentScale = headless && externalScale ? externalScale : scale;

  const showFlash = (msg, type = 'success') => {
    setFlashMsg({ msg, type });
    setTimeout(() => setFlashMsg(null), 2500);
  };

  useEffect(() => {
    console.log('📄 SimplePDFViewer: Chargement du PDF...', pdfConfig);
    
    // Charger le PDF
    const loadingTask = pdfjsLib.getDocument({
      url: pdfConfig.url,
      httpHeaders: pdfConfig.httpHeaders,
    });

    loadingTask.promise
      .then((pdfDoc) => {
        console.log('✅ PDF chargé, pages:', pdfDoc.numPages);
        setPdf(pdfDoc);
        setNumPages(pdfDoc.numPages);
        setPageNum(1);
        setLoading(false);
        if (onPdfLoaded) onPdfLoaded({ numPages: pdfDoc.numPages });
      })
      .catch((err) => {
        console.error('❌ Erreur chargement PDF:', err);
        setError(err.message);
        setLoading(false);
      });
  }, [pdfConfig]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    console.log('🎨 Rendu de la page', pageNum);

    let renderTask = null;

    pdf.getPage(currentPage).then((page) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const context = canvas.getContext('2d');
      
      // Utiliser devicePixelRatio pour une meilleure résolution
      const devicePixelRatio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: currentScale * devicePixelRatio });
      
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      canvas.style.width = `${viewport.width / devicePixelRatio}px`;
      canvas.style.height = `${viewport.height / devicePixelRatio}px`;

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      // Annuler le rendu précédent s'il existe
      if (renderTask) {
        renderTask.cancel();
      }

      renderTask = page.render(renderContext);
      
      renderTask.promise
        .then(() => {
          console.log('✅ Page', currentPage, 'rendue');
        })
        .catch((err) => {
          if (err.name === 'RenderingCancelledException') {
            console.log('⚠️ Rendu annulé (normal lors du changement de page)');
          } else {
            console.error('❌ Erreur rendu:', err);
          }
        });
    });

    // Cleanup : annuler le rendu si le composant change
    return () => {
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdf, currentPage, currentScale]);

  // Fonction de téléchargement
  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Tracker le téléchargement
      const token = searchParams.get('token');
      if (token) {
        await trackDownload(token);
        console.log('📊 Téléchargement tracké');
      }
      
      const response = await fetch(pdfConfig.url, {
        headers: pdfConfig.httpHeaders,
      });
      
      // Extraire le nom de fichier du header Content-Disposition
      let downloadFilename = 'CV.pdf'; // Valeur par défaut
      const contentDisposition = response.headers.get('Content-Disposition');
      console.log('🔍 Content-Disposition header:', contentDisposition);
      
      if (contentDisposition) {
        // Essayer d'abord le format UTF-8 (filename*=UTF-8''...)
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
        console.log('🔍 UTF-8 match:', filenameStarMatch);
        
        if (filenameStarMatch) {
          downloadFilename = decodeURIComponent(filenameStarMatch[1]);
          console.log('✅ Nom extrait (UTF-8):', downloadFilename);
        } else {
          // Sinon, essayer le format classique (filename="...")
          const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
          console.log('🔍 Classic match:', filenameMatch);
          
          if (filenameMatch) {
            downloadFilename = filenameMatch[1];
            console.log('✅ Nom extrait (classic):', downloadFilename);
          }
        }
        console.log('📝 Nom de fichier final:', downloadFilename);
      } else {
        console.log('⚠️ Aucun header Content-Disposition trouvé');
      }
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      console.log('PDF téléchargé:', downloadFilename);
      showFlash('Téléchargement terminé');
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      showFlash('Erreur lors du téléchargement', 'error');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Chargement du PDF...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center text-red-600">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-xl font-semibold">Erreur: {error}</p>
        </div>
      </div>
    );
  }

  // Mode headless : pas de toolbar, juste le canvas
  if (headless) {
    return (
      <div className="flex-1 overflow-auto flex justify-center p-4 bg-gray-100">
        <canvas ref={canvasRef} className="shadow-lg bg-white" />
      </div>
    );
  }

  // Mode standalone (V1) : toolbar intégrée
  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Flash messages */}
      {flashMsg && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-lg text-sm font-medium transition-all ${
          flashMsg.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          {flashMsg.msg}
        </div>
      )}

      {/* Barre de contrôles */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        {/* Page navigation */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPageNum((p) => Math.max(p - 1, 1))}
            disabled={pageNum <= 1}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm text-gray-600 font-medium min-w-[70px] text-center">
            {pageNum} / {numPages}
          </span>
          <button
            onClick={() => setPageNum((p) => Math.min(p + 1, numPages))}
            disabled={pageNum >= numPages}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Zoom + Télécharger */}
        <div className="flex items-center space-x-2">
          <button onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))} disabled={scale <= 0.5}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Zoom arrière">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <span className="text-xs text-gray-500 min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale((s) => Math.min(s + 0.2, 2.0))} disabled={scale >= 2.0}
            className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors" title="Zoom avant">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
          {allowDownload && (
            <button onClick={handleDownload} disabled={downloading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span>Télécharger</span>
              {downloading && <div className="w-3 h-3 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />}
            </button>
          )}
        </div>
      </div>

      {/* Canvas pour le PDF */}
      <div className="flex-1 overflow-auto flex justify-center p-4 bg-gray-100">
        <canvas ref={canvasRef} className="shadow-lg bg-white" />
      </div>
    </div>
  );
}
